import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UseSimpleVoiceChatReturn {
  isListening: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  currentTranscript: string;
  lastResponse: string;
  startListening: () => void;
  stopListening: () => void;
  error: string | null;
}

export const useSimpleVoiceChat = (positId: string): UseSimpleVoiceChatReturn => {
  const { user } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [lastResponse, setLastResponse] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<any>(null);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize speech recognition
  const initializeRecognition = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser');
      return null;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    recognition.onstart = () => {
      setIsListening(true);
      setError(null);
    };

    recognition.onresult = (event) => {
      let finalTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        }
      }
      
      if (finalTranscript.trim()) {
        setCurrentTranscript(finalTranscript.trim());
        
        // Clear previous timeout
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
        
        // Process after 1.5 seconds of silence
        silenceTimeoutRef.current = setTimeout(() => {
          processTranscript(finalTranscript.trim());
        }, 1500);
      }
    };

    recognition.onerror = (event) => {
      setError(`Speech recognition error: ${event.error}`);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
    };

    return recognition;
  }, []);

  // Process transcript and get AI response
  const processTranscript = useCallback(async (transcript: string) => {
    if (!transcript || isProcessing) return;

    setIsProcessing(true);
    setCurrentTranscript('');

    try {
      // Stop any currently playing audio
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
        setIsSpeaking(false);
      }

      // Get AI response
      const { data: chatResponse, error: chatError } = await supabase.functions.invoke('chat-with-posit-v4-unified', {
        body: {
          query: transcript,
          posit_id: positId,
          user_id: user?.id,
          voiceMode: true
        }
      });

      if (chatError) {
        throw new Error(`Chat error: ${chatError.message}`);
      }

      const responseText = chatResponse?.content || chatResponse?.response || 'I\'m sorry, I couldn\'t process that.';
      setLastResponse(responseText);


      // Convert to speech using OpenAI TTS
      await playTextAsAudio(responseText);

    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
    } finally {
      setIsProcessing(false);
    }
  }, [positId, user?.id, isProcessing]);

  // Play text as audio using OpenAI TTS
  const playTextAsAudio = useCallback(async (text: string) => {
    try {
      setIsSpeaking(true);

      const { data, error } = await supabase.functions.invoke('openai-tts', {
        body: {
          text: text,
          voice: 'alloy', // Can be: alloy, echo, fable, onyx, nova, shimmer
          model: 'tts-1',
          response_format: 'mp3'
        }
      });

      if (error) {
        throw new Error(`TTS error: ${error.message}`);
      }

      if (!data?.audio_url) {
        throw new Error('No audio URL received from TTS service');
      }

      // Play the audio
      const audio = new Audio(data.audio_url);
      currentAudioRef.current = audio;

      audio.onended = () => {
        setIsSpeaking(false);
        currentAudioRef.current = null;
      };

      audio.onerror = () => {
        setIsSpeaking(false);
        currentAudioRef.current = null;
        setError('Audio playback failed');
      };

      await audio.play();

    } catch (error) {
      setError(error instanceof Error ? error.message : 'TTS failed');
      setIsSpeaking(false);
    }
  }, []);

  // Start listening
  const startListening = useCallback(() => {
    if (isListening || isProcessing) return;

    setError(null);
    const recognition = initializeRecognition();
    
    if (recognition) {
      recognitionRef.current = recognition;
      recognition.start();
    }
  }, [isListening, isProcessing, initializeRecognition]);

  // Stop listening
  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
      setIsSpeaking(false);
    }

    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    setIsListening(false);
    setCurrentTranscript('');
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
    };
  }, [stopListening]);

  return {
    isListening,
    isProcessing,
    isSpeaking,
    currentTranscript,
    lastResponse,
    startListening,
    stopListening,
    error
  };
};